import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { MessageSquare, Plus, Eye, Clock, User, ArrowLeft, Trash2, Edit, Pin } from 'lucide-react';
import SummerNoteEditor from './admin/SummerNoteEditor';
import { useNavigate, useParams } from 'react-router-dom';

const ForumManagement = ({ userRole, isAdmin, isModerator, isAdminSpace = false, initialTopicId = null }) => {
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const navigate = useNavigate();
  const { topicId: urlTopicId } = useParams();
  
  // Utiliser initialTopicId (passé par EspaceMembre) ou urlTopicId (pour AdminDashboard)
  const topicId = initialTopicId || urlTopicId;
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateTopic, setShowCreateTopic] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [newTopic, setNewTopic] = useState({ title: '', content: '', category: '' });
  const [newPost, setNewPost] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);

  // Récupérer l'ID de l'utilisateur connecté
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('currentUser') || '{}');
    setCurrentUserId(userData.id);
  }, []);

  // Définir les catégories disponibles selon le rôle et l'espace
  useEffect(() => {
    console.log('ForumManagement - isAdmin:', isAdmin, 'isModerator:', isModerator, 'isAdminSpace:', isAdminSpace);
    
    const availableCategories = [
      { id: 'Général', name: 'Général', icon: '💬' },
      { id: 'Lignage', name: 'Lignage', icon: '🏛️' }
    ];

    // Modérateur visible pour modérateurs et admins
    if (isModerator || isAdmin) {
      availableCategories.push({ id: 'Modérateur', name: 'Modérateur', icon: '🛡️' });
      console.log('Ajouté catégorie Modérateur');
    }

    // Admin visible UNIQUEMENT dans l'espace admin
    if (isAdmin && isAdminSpace) {
      availableCategories.push({ id: 'Admin', name: 'Admin', icon: '👑' });
      console.log('Ajouté catégorie Admin (espace admin)');
    }

    console.log('Categories disponibles:', availableCategories);
    setCategories(availableCategories);
    
    // Sélectionner "Général" par défaut
    if (availableCategories.length > 0) {
      setSelectedCategory(availableCategories[0].id);
    }
  }, [isAdmin, isModerator, isAdminSpace]);

  // Charger les topics d'une catégorie
  useEffect(() => {
    if (selectedCategory) {
      loadTopics(selectedCategory);
    }
  }, [selectedCategory]);

  // Charger le topic depuis l'URL si topicId est présent
  useEffect(() => {
    if (topicId) {
      loadTopic(topicId);
    }
  }, [topicId, initialTopicId]); // Ajouter initialTopicId comme dépendance

  const loadTopics = async (category) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('memberToken') || localStorage.getItem('authToken') || localStorage.getItem('token');
      console.log('loadTopics - category:', category, 'token présent:', !!token);
      const res = await fetch(`${API_BASE}/api/forum/topics?category=${category}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        console.error('Erreur API:', res.status, res.statusText);
        throw new Error('Erreur chargement topics');
      }
      const data = await res.json();
      console.log('Topics reçus:', data.topics?.length || 0);
      setTopics(data.topics || []);
    } catch (error) {
      console.error('Erreur loadTopics:', error);
      setTopics([]);
    } finally {
      setLoading(false);
    }
  };

  // Charger un topic avec ses posts
  const loadTopic = async (topicId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('memberToken') || localStorage.getItem('authToken') || localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/forum/topics/${topicId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erreur chargement topic');
      const data = await res.json();
      setSelectedTopic(data.topic);
      setPosts(data.posts || []);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  // Créer un nouveau topic
  const handleCreateTopic = async () => {
    if (!newTopic.title.trim() || !newTopic.content.trim()) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/forum/topics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          category: selectedCategory,
          title: newTopic.title,
          content: newTopic.content
        })
      });

      if (!res.ok) throw new Error('Erreur création topic');

      // Réinitialiser et recharger
      setNewTopic({ title: '', content: '', category: '' });
      setShowCreateTopic(false);
      loadTopics(selectedCategory);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la création du topic');
    }
  };

  // Modifier un topic existant
  const handleUpdateTopic = async () => {
    if (!editingTopic.title.trim() || !editingTopic.content.trim()) {
      alert('Le titre et le contenu sont requis');
      return;
    }

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/forum/topics/${editingTopic.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          category: editingTopic.category,
          title: editingTopic.title,
          content: editingTopic.content
        })
      });

      if (!res.ok) throw new Error('Erreur modification topic');

      // Réinitialiser et recharger
      setEditingTopic(null);
      if (selectedTopic && selectedTopic.id === editingTopic.id) {
        loadTopic(editingTopic.id);
      } else {
        loadTopics(selectedCategory);
      }
      alert('Topic modifié avec succès');
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la modification du topic');
    }
  };


  // Créer un nouveau post
  const handleCreatePost = async () => {
    if (!newPost.trim()) {
      alert('Veuillez écrire un message');
      return;
    }

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/forum/topics/${selectedTopic.id}/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: newPost })
      });

      if (!res.ok) throw new Error('Erreur création post');

      // Réinitialiser et recharger
      setNewPost('');
      loadTopic(selectedTopic.id);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la création du post');
    }
  };

  // Supprimer un topic
  const handleDeleteTopic = async (topicId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce topic ?')) return;

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/forum/topics/${topicId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Erreur suppression topic');

      loadTopics(selectedCategory);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleTogglePin = async (topicId, currentPinStatus, e) => {
    e.stopPropagation(); // Empêcher de cliquer sur le topic lui-même
    
    try {
      const token = localStorage.getItem('memberToken') || localStorage.getItem('authToken') || localStorage.getItem('token');
      const newPinStatus = !currentPinStatus;
      
      const res = await fetch(`${API_BASE}/api/forum/topics/${topicId}/pin?is_pinned=${newPinStatus}`, {
        method: 'PATCH',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Erreur épinglage topic');
      }

      // Recharger les topics pour voir le changement
      loadTopics(selectedCategory);
      
      const action = newPinStatus ? 'épinglé' : 'désépinglé';
      console.log(`Topic ${action} avec succès`);
    } catch (error) {
      console.error('Erreur:', error);
      alert(`Erreur lors de l'épinglage: ${error.message}`);
    }
  };


  // Supprimer un post
  const handleDeletePost = async (postId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette réponse ?')) return;

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/forum/posts/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Erreur suppression post');

      // Recharger le topic
      loadTopic(selectedTopic.id);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };


  // Format de date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Vue: Liste des topics
  if (!selectedTopic) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
            Forum
          </h1>
          <p className="text-gray-600">Échangez avec les membres de l'association</p>
        </div>

        {/* Catégories */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {categories.map((cat) => (
            <Button
              key={cat.id}
              variant={selectedCategory === cat.id ? 'default' : 'outline'}
              onClick={() => setSelectedCategory(cat.id)}
              style={{
                fontFamily: '"Lora", serif',
                backgroundColor: selectedCategory === cat.id ? 'rgb(196, 150, 96)' : 'transparent',
                borderColor: 'rgb(196, 150, 96)',
                color: selectedCategory === cat.id ? 'white' : 'rgb(196, 150, 96)'
              }}
            >
              <span className="mr-2">{cat.icon}</span>
              {cat.name}
            </Button>
          ))}
        </div>

        {/* Message de bienvenue selon la catégorie */}
        <Card className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="text-4xl">
                {selectedCategory === 'Général' && '💬'}
                {selectedCategory === 'Lignage' && '🏛️'}
                {selectedCategory === 'Modérateur' && '🛡️'}
                {selectedCategory === 'Admin' && '👑'}
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
                  {selectedCategory === 'Général' && 'Bienvenue sur le forum Général'}
                  {selectedCategory === 'Lignage' && 'Bienvenue sur le forum Lignage'}
                  {selectedCategory === 'Modérateur' && 'Espace Modérateurs'}
                  {selectedCategory === 'Admin' && 'Espace Administrateurs'}
                </h3>
                <p className="text-gray-700" style={{ fontFamily: '"Lora", serif' }}>
                  {selectedCategory === 'Général' && 
                    'Espace de discussion ouvert à tous les membres pour échanger sur la vie de l\'association, partager vos idées et poser vos questions.'}
                  {selectedCategory === 'Lignage' && 
                    'Discutez de l\'histoire des lignages de Bruxelles, partagez vos recherches généalogiques et découvrez le patrimoine de nos familles.'}
                  {selectedCategory === 'Modérateur' && 
                    'Espace réservé aux modérateurs pour coordonner la modération des forums et discuter des aspects techniques de la gestion des contenus.'}
                  {selectedCategory === 'Admin' && 
                    'Espace réservé aux administrateurs pour les discussions relatives à la gestion de l\'association et les décisions stratégiques.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bouton Nouveau Topic */}
        {!showCreateTopic && (
          <Button
            onClick={() => setShowCreateTopic(true)}
            style={{
              fontFamily: '"Lora", serif',
              backgroundColor: 'rgb(196, 150, 96)',
              color: 'white'
            }}
            className="mb-4"
          >
            <Plus size={18} className="mr-2" />
            Nouveau sujet
          </Button>
        )}

        {/* Formulaire Nouveau Topic */}
        {showCreateTopic && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
                Créer un nouveau sujet
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Titre</label>
                  <input
                    type="text"
                    value={newTopic.title}
                    onChange={(e) => setNewTopic({ ...newTopic, title: e.target.value })}
                    className="w-full p-2 border rounded"
                    placeholder="Titre du sujet"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Message</label>
                  <SummerNoteEditor
                    value={newTopic.content}
                    onChange={(content) => setNewTopic({ ...newTopic, content })}
                    height={300}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleCreateTopic}
                    style={{
                      fontFamily: '"Lora", serif',
                      backgroundColor: 'rgb(196, 150, 96)',
                      color: 'white'
                    }}
                  >
                    Créer le sujet
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCreateTopic(false);
                      setNewTopic({ title: '', content: '', category: '' });
                    }}
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Liste des Topics */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Chargement...</p>
          </div>
        ) : topics.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">Aucun sujet dans cette catégorie</p>
            <p className="text-gray-400 text-sm">Soyez le premier à créer un sujet !</p>
          </div>
        ) : (
          <div className="space-y-2">
            {topics.map((topic) => (
              <Card
                key={topic.id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => {
                  if (isAdminSpace) {
                    // En admin, on charge le topic directement sans navigate
                    loadTopic(topic.id);
                  } else {
                    // En membre, on utilise navigate pour avoir une URL propre
                    navigate(`/membre/forum/topic/${topic.id}`);
                  }
                }}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {topic.is_pinned && (
                          <Pin size={18} className="text-amber-600" title="Sujet épinglé" />
                        )}
                        <h3 className="font-semibold text-lg" style={{ fontFamily: '"Lora", serif' }}>
                          {topic.title}
                        </h3>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <User size={14} />
                          {topic.author_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare size={14} />
                          {topic.replies_count} réponse{topic.replies_count > 1 ? 's' : ''}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye size={14} />
                          {topic.views_count} vue{topic.views_count > 1 ? 's' : ''}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {formatDate(topic.last_activity_at)}
                        </span>
                      </div>
                    </div>
                    {(isAdmin || isModerator) && (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleTogglePin(topic.id, topic.is_pinned, e)}
                          className={topic.is_pinned ? "text-amber-600 hover:text-amber-700" : "text-gray-500 hover:text-gray-700"}
                          title={topic.is_pinned ? "Désépingler ce sujet" : "Épingler ce sujet"}
                        >
                          <Pin size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTopic(topic.id);
                          }}
                          className="text-red-500 hover:text-red-700"
                          title="Supprimer ce sujet"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Vue: Topic détaillé avec posts
  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Bouton retour */}
      <Button
        variant="outline"
        onClick={() => {
          setSelectedTopic(null);
          setPosts([]);
          if (!isAdminSpace) {
            // En membre, on utilise navigate
            navigate('/membre/forum');
          }
          // En admin, on reste sur place, juste on réinitialise l'état
        }}
        className="mb-4"
      >
        <ArrowLeft size={18} className="mr-2" />
        Retour aux sujets
      </Button>

      {/* Formulaire de modification si editingTopic */}
      {editingTopic && (
        <Card className="mb-6 border-2 border-amber-300">
          <CardHeader>
            <CardTitle style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
              Modifier le topic
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Titre</label>
                <input
                  type="text"
                  value={editingTopic.title}
                  onChange={(e) => setEditingTopic({...editingTopic, title: e.target.value})}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Contenu</label>
                <SummerNoteEditor
                  value={editingTopic.content}
                  onChange={(content) => setEditingTopic({...editingTopic, content})}
                  height="200px"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleUpdateTopic} style={{ backgroundColor: 'rgb(34, 197, 94)' }}>
                  Enregistrer
                </Button>
                <Button variant="outline" onClick={() => setEditingTopic(null)}>
                  Annuler
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Topic principal */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
                {selectedTopic.title}
              </CardTitle>
              <div className="flex items-center gap-4 text-sm text-gray-500 mt-2">
                <span className="flex items-center gap-1">
                  <User size={14} />
                  {selectedTopic.author_name}
                </span>
                <span className="flex items-center gap-1">
                  <Clock size={14} />
                  {formatDate(selectedTopic.created_at)}
                </span>
              </div>
            </div>
            {/* Bouton Modifier si l'utilisateur est l'auteur, modérateur ou admin */}
            {(currentUserId === selectedTopic.author_id || isAdmin || isModerator) && !editingTopic && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingTopic({
                    id: selectedTopic.id,
                    title: selectedTopic.title,
                    content: selectedTopic.content,
                    category: selectedTopic.category
                  });
                }}
                className="flex items-center gap-1"
              >
                <Edit size={16} />
                Modifier
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div 
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: selectedTopic.content }}
          />
        </CardContent>
      </Card>

      {/* Posts */}
      <div className="space-y-4 mb-6">
        {posts.map((post) => (
          <Card key={post.id}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <User size={14} />
                  <span className="font-medium">{post.author_name}</span>
                  <span>•</span>
                  <Clock size={14} />
                  <span>{formatDate(post.created_at)}</span>
                </div>
                {(isAdmin || isModerator) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeletePost(post.id)}
                    className="text-red-500 hover:text-red-700"
                    title="Supprimer cette réponse"
                  >
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
              <div 
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Formulaire Répondre */}
      <Card>
        <CardHeader>
          <CardTitle style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
            Répondre au sujet
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <SummerNoteEditor
              value={newPost}
              onChange={setNewPost}
              height={250}
            />
            <Button
              onClick={handleCreatePost}
              style={{
                fontFamily: '"Lora", serif',
                backgroundColor: 'rgb(196, 150, 96)',
                color: 'white'
              }}
            >
              <MessageSquare size={18} className="mr-2" />
              Publier la réponse
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForumManagement;
